import React from 'react';
import moment from 'moment';
import gql from 'graphql-tag';
import { Moment } from 'moment/moment';
import { useQuery } from '@apollo/client';
import { useSelector } from 'react-redux';
import { QueryListResponse } from 'global';
import { useForm, useWatch } from 'react-hook-form';
import { Box, Stack, Typography } from '@mui/material';
import { accountSelectors } from 'redux/accountReducer';
import { DateRangeWithPeriods, SelectField } from 'components/form';
import { PageContainer } from 'components/shared/Page/PageContainer';
import { CardContainer } from 'components/shared/CardContainer/CardContainer';

import { bpfAPI } from '../../api';
import { BPFAPINameSpase } from '../../types';

export const BPFReports = () => {
  const account = useSelector(accountSelectors.account);

  const { control, setValue } = useForm<{ filters: { department: string; to: Moment; from: Moment } }>({
    mode: 'onChange',
    defaultValues: {
      filters: {
        department: 'SALES',
        to: moment().endOf('month'),
        from: moment().startOf('month'),
      },
    },
  });

  const filters = useWatch({ control, name: 'filters' });

  const { data: departmentsResponse, loading } = useQuery(bpfAPI.department.fetchDepartmentsOptions);

  const { data } = useQuery<BPFAPINameSpase<QueryListResponse<{ templateId: string; attachedEntities: string[] }>>>(
    gql`
      query GetReport($input: BPFDepartmentReportInput!) {
        bpf {
          result: pullDepartmentReport(input: $input) {
            error
            success
            list {
              templateId
              attachedEntities
            }
          }
        }
      }
    `,
    {
      skip: !account.id || !account.vendorProfileId,
      notifyOnNetworkStatusChange: true,
      variables: {
        input: {
          filters: {
            department: filters.department,
            created: {
              to: filters.to.toISOString(),
              from: filters.from.toISOString(),
            },
          },
          accountId: account.id,
          vendorProfileId: account.vendorProfileId,
        },
      },
    }
  );

  const handleSelectRange = async (range: { endDate: Moment; startDate: Moment }) => {
    setValue('filters.to', range.endDate);
    setValue('filters.from', range.startDate);
  };

  return (
    <PageContainer title="BPF Report">
      <CardContainer>
        <Stack gap="0.5rem">
          <SelectField
            required
            control={control}
            variant="outlined"
            label="Department"
            name="filters.department"
            isLoading={loading}
            options={departmentsResponse?.bpf?.departments?.list ?? []}
          />

          <DateRangeWithPeriods
            control={control}
            nameTo="filters.to"
            nameFrom="filters.from"
            onRangeChange={handleSelectRange}
          />
        </Stack>

        <Box>
          {(data?.bpf?.result?.list ?? []).map((reportItem: any) => (
            <Stack direction="row" gap="0.5rem">
              <Typography>{reportItem.templateId}</Typography>
              <Typography>{reportItem.attachedEntities}</Typography>
            </Stack>
          ))}
        </Box>
      </CardContainer>
    </PageContainer>
  );
};
